/**
 * useHealth Hook
 * Derives health diagnostics from forecast account data
 *
 * Note: No backend API exists for health - metrics are derived from forecast accounts
 */

import { useMemo } from 'react';
import type { HealthItem, HealthIssue, DriverType } from '../types';
import { useForecastData } from './useForecastData';

// ============ Query Keys ============

export const healthQueryKeys = {
  all: ['forecast-v3', 'health'] as const,
  status: (orgId: number, locationId: number) =>
    [...healthQueryKeys.all, 'status', { orgId, locationId }] as const,
};

// ============ Hook Parameters ============

interface UseHealthParams {
  orgId: number;
  locationId: number;
  versionId?: number | null;
  year?: number;
  enabled?: boolean;
}

// ============ Return Types ============

interface UseHealthReturn {
  healthItems: HealthItem[];
  issues: HealthIssue[];
  overallStatus: 'good' | 'warning' | 'critical';
  driverCoverage: number;
  issueCount: number;
  highPriorityCount: number;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

// ============ Helper Functions ============

/**
 * Check if a driver type is considered "configured" (not manual/unassigned)
 */
function isDriverConfigured(driverType: DriverType): boolean {
  return driverType !== 'Manual' && driverType !== 'Static Entry';
}

/**
 * Check if an account has missing data (all zeros)
 */
function hasMissingData(monthlyValues: Record<string, number>): boolean {
  const values = Object.values(monthlyValues);
  if (values.length === 0) return true;
  return values.every(v => v === 0 || v === null || v === undefined);
}

/**
 * Determine issue severity based on account type and issue
 */
function getIssueSeverity(accountType: string, issueType: string): 'high' | 'medium' | 'low' {
  if (issueType === 'no_driver') {
    return accountType === 'Revenue' ? 'high' : 'medium';
  }
  if (issueType === 'missing_data') {
    return 'medium';
  }
  return 'low';
}

// ============ Main Hook ============

export function useHealth({
  orgId,
  locationId,
  versionId,
  year = new Date().getFullYear(),
  enabled = true,
}: UseHealthParams): UseHealthReturn {
  // Get forecast data which includes accounts
  // Pass versionId to use V3 Calculate API for accurate driver assignments
  const {
    accounts,
    isLoading,
    error,
    refetch,
  } = useForecastData({
    orgId,
    locationId,
    year,
    versionId,
    requireVersion: true,
    enabled,
  });

  // Calculate health metrics
  const { healthItems, issues, driverCoverage } = useMemo(() => {
    if (!accounts || accounts.length === 0) {
      return {
        healthItems: getDefaultHealthItems(),
        issues: [],
        driverCoverage: 0,
      };
    }

    const totalAccounts = accounts.length;
    const configuredAccounts = accounts.filter(a => isDriverConfigured(a.driverType)).length;
    const coverage = totalAccounts > 0 ? Math.round((configuredAccounts / totalAccounts) * 100) : 0;

    // Identify issues
    const derivedIssues: HealthIssue[] = [];
    let issueId = 1;

    accounts.forEach(account => {
      // Check for no driver assigned
      if (!isDriverConfigured(account.driverType)) {
        derivedIssues.push({
          id: issueId++,
          account: account.accountName,
          accountId: account.chartOfAccountId,
          issue: 'No driver assigned',
          severity: getIssueSeverity(account.accountType, 'no_driver'),
        });
      }

      // Check for missing data
      if (hasMissingData(account.monthlyValues)) {
        derivedIssues.push({
          id: issueId++,
          account: account.accountName,
          accountId: account.chartOfAccountId,
          issue: 'No forecast data for any month',
          severity: getIssueSeverity(account.accountType, 'missing_data'),
        });
      }
    });

    // Sort issues by severity
    const severityOrder = { high: 0, medium: 1, low: 2 };
    derivedIssues.sort((a, b) => severityOrder[a.severity] - severityOrder[b.severity]);

    // Count by driver type
    const driverCounts: Record<string, number> = {};
    accounts.forEach(a => {
      driverCounts[a.driverType] = (driverCounts[a.driverType] || 0) + 1;
    });

    // Build health items
    const items: HealthItem[] = [
      {
        id: 'coverage',
        label: 'Driver Coverage',
        value: `${coverage}%`,
        status: coverage >= 90 ? 'good' : coverage >= 70 ? 'warning' : 'error',
        detail: `${configuredAccounts} of ${totalAccounts} accounts`,
      },
      {
        id: 'accounts',
        label: 'Total Accounts',
        value: totalAccounts.toString(),
        status: 'good',
        detail: `Revenue: ${accounts.filter(a => a.accountType === 'Revenue').length}, Expense: ${accounts.filter(a => a.accountType === 'Expense').length}`,
      },
      {
        id: 'issues',
        label: 'Issues Found',
        value: derivedIssues.length.toString(),
        status: derivedIssues.length === 0 ? 'good' : derivedIssues.some(i => i.severity === 'high') ? 'error' : 'warning',
        detail: `${derivedIssues.filter(i => i.severity === 'high').length} high priority`,
      },
      {
        id: 'drivers',
        label: 'Driver Types Used',
        value: Object.keys(driverCounts).filter(d => d !== 'Manual').length.toString(),
        status: 'good',
        detail: 'Different driver types configured',
      },
    ];

    return {
      healthItems: items,
      issues: derivedIssues,
      driverCoverage: coverage,
    };
  }, [accounts]);

  // Calculate high priority count
  const highPriorityCount = useMemo(() => {
    return issues.filter(i => i.severity === 'high').length;
  }, [issues]);

  // Determine overall status
  const overallStatus = useMemo((): 'good' | 'warning' | 'critical' => {
    if (highPriorityCount > 5) return 'critical';
    if (highPriorityCount > 0 || driverCoverage < 70) return 'warning';
    return 'good';
  }, [highPriorityCount, driverCoverage]);

  return {
    healthItems,
    issues,
    overallStatus,
    driverCoverage,
    issueCount: issues.length,
    highPriorityCount,
    isLoading,
    error: error as Error | null,
    refetch,
  };
}

/**
 * Default health items when no data is available
 */
function getDefaultHealthItems(): HealthItem[] {
  return [
    {
      id: 'coverage',
      label: 'Driver Coverage',
      value: '0%',
      status: 'warning',
      detail: 'No accounts loaded',
    },
    {
      id: 'accounts',
      label: 'Total Accounts',
      value: '0',
      status: 'warning',
      detail: 'No forecast data available',
    },
    {
      id: 'issues',
      label: 'Issues Found',
      value: '0',
      status: 'good',
      detail: 'No issues detected',
    },
    {
      id: 'drivers',
      label: 'Driver Types Used',
      value: '0',
      status: 'warning',
      detail: 'No drivers configured',
    },
  ];
}

export default useHealth;
